import { useState } from 'react';

import {
  Card,
  Stack,
  Pagination,
  CardHeader,
  Typography,
  CardContent,
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { openStackedRecord } from 'src/store/slices/app/appSlice';
import { useGetLinkedDealsQuery } from 'src/services/deal-flow/deal-flow.service';

import Label from 'src/components/label';
import ListSkeleton from 'src/components/list/list-skeleton';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

const PER_PAGE = 5;

type Props = {
  contactId?: string;
  companyProfileId?: string;
};

/**
 * JT1-1736: the deals a contact or company is linked to, on its profile sidebar. Display-only — deals
 * are still linked from the deal itself. A deal opens on top of the profile rather than leaving it.
 */
export function LinkedDealsCard({ contactId, companyProfileId }: Props) {
  const { t } = useTranslate();

  const [page, setPage] = useState(1);

  const dispatch = useAppDispatch();

  // Deal flow exists for recruiter and client tenants only (see the dashboard navigation).
  const hasDeals = [TenantType.Recruiter, TenantType.Client].includes(useOrgTenant());

  const { currentData, isLoading, isError } = useGetLinkedDealsQuery(
    { contactId, companyProfileId, page, perPage: PER_PAGE },
    { skip: !hasDeals || (!contactId && !companyProfileId) }
  );

  const deals = currentData?.results ?? [];
  const count = currentData?.count ?? 0;

  if (!hasDeals || isError) {
    return null;
  }

  return (
    <Card>
      <CardHeader title={t('deal_flow.linked_deals.title')} />
      <CardContent>
        {isLoading && <ListSkeleton height={40} />}

        {!isLoading && !deals.length && (
          <Typography variant="body2" color="text.secondary">
            {t('deal_flow.linked_deals.empty')}
          </Typography>
        )}

        {!isLoading && !!deals.length && (
          <Stack spacing={1.5}>
            {deals.map((deal) => (
              <Stack key={deal.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    onClick={() => dispatch(openStackedRecord({ type: 'deal', id: deal.id }))}
                    sx={{ cursor: 'pointer', textTransform: 'capitalize', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {deal.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {deal.value ? fCurrency(deal.value, deal.pipeline?.currency?.code) : '--'}
                  </Typography>
                </Stack>

                {deal.stage && (
                  <Label variant="soft" sx={{ bgcolor: deal.stage.color, color: 'white', flexShrink: 0 }}>
                    {deal.stage.name}
                  </Label>
                )}
              </Stack>
            ))}

            {count > PER_PAGE && (
              <Stack direction="row" justifyContent="center">
                <Pagination
                  size="small"
                  page={page}
                  count={Math.ceil(count / PER_PAGE)}
                  onChange={(_e, value) => setPage(value)}
                />
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
